import React, { useCallback, useContext, useEffect, useReducer } from 'react'

import { setsEq } from 'utils/sets'

import {
  defaultCurrencyOptions,
  defaultQFManagerContextState,
  stateReducer,
} from './state'
import {
  CycleDirection,
  FormatterOption,
  QFManagerContextType as QFContextType,
} from './types'

/**
 * Use to supply a default function to a context. If called, it will log an
 * error to the console, noting that the function isn't implemented in this
 * context
 */
const defaultCtxFn = (ctxName: string) => (fnName: string) => () => {
  console.error(
    `${ctxName} -> ${fnName}: function not available, you are in the default context`,
  )
}

const defaultFn = defaultCtxFn('QuantityFormattingContext')

export const QFManagerContext = React.createContext<QFContextType>({
  currencyOptions: [...defaultCurrencyOptions],
  setCurrencyOptions: defaultFn('setCurrencyOptions'),
  resetCurrencyOptions: defaultFn('resetCurrencyOptions'),
  selectedCurrency: 'Native',
  setSelectedCurrency: defaultFn('setSelectedCurrency'),
  cycleSelectedCurrency: defaultFn('cycleSelectedCurrency'),
  selectedFormatter: 'Compact',
  setSelectedFormatter: defaultFn('setSelectedFormatter'),
  cycleSelectedFormatter: defaultFn('cycleSelectedFormatter'),
})

export const QuantityFormattingProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(
    stateReducer,
    defaultQFManagerContextState,
  )

  const setCurrencyOptions = useCallback(
    (currencyOptions: string[], fallbackCurrency?: string) =>
      dispatch({
        type: 'set_currency_options',
        currencyOptions,
        fallbackCurrency,
      }),
    [],
  )

  const resetCurrencyOptions = useCallback(
    () =>
      dispatch({
        type: 'set_currency_options',
        currencyOptions: defaultCurrencyOptions,
      }),
    [],
  )

  const setSelectedCurrency = useCallback(
    (selectedCurrency: string) =>
      dispatch({
        type: 'set_selected_currency',
        selectedCurrency,
      }),
    [],
  )

  const cycleSelectedCurrency = useCallback(
    (direction: CycleDirection) =>
      dispatch({ type: 'cycle_selected_currency', direction }),
    [],
  )

  const setSelectedFormatter = useCallback(
    (selectedFormatter: FormatterOption) =>
      dispatch({ type: 'set_selected_formatter', selectedFormatter }),
    [],
  )

  const cycleSelectedFormatter = useCallback(
    (direction: CycleDirection) =>
      dispatch({ type: 'cycle_selected_formatter', direction }),
    [],
  )

  return (
    <QFManagerContext.Provider
      value={{
        ...state,
        setCurrencyOptions,
        resetCurrencyOptions,
        setSelectedCurrency,
        cycleSelectedCurrency,
        setSelectedFormatter,
        cycleSelectedFormatter,
      }}
    >
      {children}
    </QFManagerContext.Provider>
  )
}

export const useQuantityFormatting = (): QFContextType =>
  useContext(QFManagerContext)

/**
 * Hook to set the currency options for a given component's lifecycle. The
 * provided options will be set while the component is mounted, and then reset
 * to default when the component unmounts (e.g. when navigating to a new route)
 */
export const useCurrencyOptions = (
  currencyOptions: string[],
  selectedCurrency?: string,
): void => {
  const {
    currencyOptions: prevCurrencyOptions,
    setCurrencyOptions,
    resetCurrencyOptions,
  } = useQuantityFormatting()
  const optsJSON = JSON.stringify(currencyOptions)
  const prevOptsJSON = JSON.stringify(prevCurrencyOptions)

  /** This runs any time options change */
  useEffect(() => {
    const opts = JSON.parse(optsJSON)
    const prevOpts = JSON.parse(prevOptsJSON)
    if (!setsEq(new Set(opts), new Set(prevOpts))) {
      setCurrencyOptions(opts, selectedCurrency)
    }
  }, [optsJSON, prevOptsJSON, selectedCurrency, setCurrencyOptions])

  /**
   * This hook only runs on unmount/remount, so will reset everything when the
   * caller unmounts
   */
  useEffect(() => {
    return resetCurrencyOptions
  }, [resetCurrencyOptions])
}
